import { api } from './client'

export type TaskStatus = 'pending' | 'in_progress' | 'completed' | 'blocked'
export type TaskPriority = 'low' | 'medium' | 'high' | 'urgent'

export interface Task {
  id: string
  title: string
  description?: string
  status: TaskStatus
  priority: TaskPriority
  due_date?: string
  client_id?: string
  client_name?: string
  assignee_id?: string
  assignee_name?: string
  statutory_type?: string
  is_billable?: boolean
  category?: string
  time_logged_minutes: number
  created_at: string
  updated_at: string
}

export interface TaskListResponse {
  items: Task[]
  total: number
  page: number
  page_size: number
}

export interface CreateTaskRequest {
  title: string
  description?: string
  priority?: TaskPriority
  due_date?: string
  client_id?: string
  assignee_id?: string
  statutory_type?: string
  is_billable?: boolean
  category?: string
}

export interface UpdateTaskRequest {
  title?: string
  description?: string
  status?: TaskStatus
  priority?: TaskPriority
  due_date?: string
  assignee_id?: string
}

export interface TaskFilters {
  status?: TaskStatus
  priority?: TaskPriority
  client_id?: string
  assignee_id?: string
  is_billable?: boolean
  category?: string
  due_before?: string
  due_after?: string
  page?: number
  per_page?: number
}

// Internal types matching actual backend response shape
interface BackendTask {
  id: string
  title: string
  description?: string
  status: TaskStatus
  priority: TaskPriority
  due_date?: string
  client_id?: string
  assigned_to?: string | null
  assignee?: { id: string; name: string; email: string } | null
  client?: { id: string; name: string } | null
  statutory_type?: string
  is_billable?: boolean
  category?: string | null
  time_logged_minutes?: number
  created_at: string
  updated_at: string
}

interface BackendTaskListResponse {
  items: BackendTask[]
  total: number
  page: number
  per_page: number
}

function mapTask(raw: BackendTask): Task {
  return {
    id: raw.id,
    title: raw.title,
    description: raw.description,
    status: raw.status,
    priority: raw.priority,
    due_date: raw.due_date,
    client_id: raw.client_id,
    client_name: raw.client?.name,
    assignee_id: raw.assigned_to ?? raw.assignee?.id,
    assignee_name: raw.assignee?.name,
    statutory_type: raw.statutory_type,
    is_billable: raw.is_billable,
    category: raw.category ?? undefined,
    time_logged_minutes: raw.time_logged_minutes ?? 0,
    created_at: raw.created_at,
    updated_at: raw.updated_at,
  }
}

function mapTaskList(raw: BackendTaskListResponse): TaskListResponse {
  return {
    items: raw.items.map(mapTask),
    total: raw.total,
    page: raw.page,
    page_size: raw.per_page,
  }
}

export const tasksApi = {
  /**
   * List tasks with optional filters
   */
  list: async (filters?: TaskFilters): Promise<TaskListResponse> => {
    const params = new URLSearchParams()
    if (filters) {
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined) {
          params.append(key, String(value))
        }
      })
    }
    const query = params.toString()
    const raw: BackendTaskListResponse = await api.get(`/api/v1/tasks${query ? `?${query}` : ''}`)
    return mapTaskList(raw)
  },

  /**
   * Get a single task by ID
   */
  get: async (id: string): Promise<Task> => {
    const raw: BackendTask = await api.get(`/api/v1/tasks/${id}`)
    return mapTask(raw)
  },

  /**
   * Create a new task
   */
  create: async (data: CreateTaskRequest): Promise<Task> => {
    const raw: BackendTask = await api.post('/api/v1/tasks', data)
    return mapTask(raw)
  },

  /**
   * Update a task
   */
  update: async (id: string, data: UpdateTaskRequest): Promise<Task> => {
    const raw: BackendTask = await api.patch(`/api/v1/tasks/${id}`, data)
    return mapTask(raw)
  },

  /**
   * Delete a task
   */
  delete: (id: string): Promise<void> => {
    return api.delete(`/api/v1/tasks/${id}`)
  },

  /**
   * Get my tasks (assigned to current user).
   * Backend auto-scopes Articles to their own tasks via /api/v1/tasks.
   */
  myTasks: async (): Promise<TaskListResponse> => {
    const raw: BackendTaskListResponse = await api.get('/api/v1/tasks?per_page=50')
    return mapTaskList(raw)
  },
}
